<template>
  <v-card outlined class="student-card">
    <!-- Top controls -->
    <v-card-text class="pb-2">
      <div class="d-flex align-center">
        <div class="font-weight-bold">Select Student</div>
        <v-spacer />

        <!-- Switch mode (only show if both IDs exist) -->
        <!-- <v-btn v-if="canSwitch" small outlined class="ml-2" @click="toggleMode">
          {{ modeLabel }}
        </v-btn> -->
      </div>

      <!-- Search -->
      <v-text-field
        v-model.trim="search"
        class="mt-3"
        dense
        outlined
        clearable
        hide-details
        label="Search student"
        prepend-inner-icon="mdi-magnify"
      />
    </v-card-text>

    <v-divider />

    <!-- List -->
    <v-list
      v-if="filteredStudents.length"
      class="list-500"
      :dense="$vuetify.breakpoint.xsOnly"
    >
      <template v-for="(item, index) in filteredStudents">
        <v-list-item
          :key="studentKey(item, index)"
          @click="studentSelected(item)"
        >
          <v-list-item-content>
            <v-list-item-title class="text-capitalize">
              {{ item.fullName }}
            </v-list-item-title>

            <!-- optional small hint -->
            <v-list-item-subtitle
              v-if="mode === 'department'"
              class="grey--text"
            >
              Department view
            </v-list-item-subtitle>
          </v-list-item-content>

          <v-list-item-action>
            <v-icon>mdi-chevron-right</v-icon>
          </v-list-item-action>
        </v-list-item>

        <v-divider
          v-if="index < filteredStudents.length - 1"
          :key="`div-${studentKey(item, index)}`"
        />
      </template>
    </v-list>

    <v-card-text v-else class="py-8 text-center grey--text">
      <div v-if="isLoading">Loading students...</div>
      <div v-else>No students found</div>
    </v-card-text>
  </v-card>
</template>

<script>
import studentService from "@/modules/Students/service.js";

export default {
  name: "student-list",
  props: {
    ClassroomId: { default: null },
    ClassId: { default: null }, // department
  },
  data() {
    return {
      students: [],
      search: "",
      isLoading: false,
      // mode: 'classroom' | 'department'
      mode: "classroom",
    };
  },
  computed: {
    canSwitch() {
      return !!this.ClassroomId && !!this.ClassId;
    },
    modeLabel() {
      // button label shows what user will switch to
      return this.mode === "classroom"
        ? "Whole Department"
        : "Only This Classroom";
    },
    filteredStudents() {
      const q = (this.search || "").toLowerCase().trim();
      if (!q) return this.students;

      return this.students.filter((s) => {
        const name = String(s.fullName || "").toLowerCase();
        return name.includes(q);
      });
    },
  },
  watch: {
    ClassroomId: {
      immediate: true,
      handler() {
        // default preference: classroom if available
        if (this.ClassroomId) this.mode = "classroom";
        else if (this.ClassId) this.mode = "department";

        this.getList();
      },
    },
    ClassId() {
      // if classroom is missing but class exists, load department
      if (!this.ClassroomId && this.ClassId) {
        this.mode = "department";
        this.getList();
      }
    },
  },
  methods: {
    studentKey(item, index) {
      // best options first
      return (
        item.id ||
        item.StudentId ||
        item.userId ||
        item._id ||
        `${item.fullName || "student"}-${index}`
      );
    },
    toggleMode() {
      this.mode = this.mode === "classroom" ? "department" : "classroom";
      this.search = "";
      this.getList();
    },

    getList() {
      // Decide query by mode, with fallback rules
      const useClassroom = this.mode === "classroom" && this.ClassroomId;

      const useDepartment = this.mode === "department" && this.ClassId;

      const params = useClassroom
        ? { ClassRoomId: this.ClassroomId }
        : useDepartment
        ? { ClassId: this.ClassId }
        : this.ClassroomId
        ? { ClassRoomId: this.ClassroomId }
        : this.ClassId
        ? { ClassId: this.ClassId }
        : null;

      if (!params) {
        this.students = [];
        return;
      }

      this.isLoading = true;

      return studentService
        .getAll(params)
        .then((response) => {
          this.students = response && response.data ? response.data : [];
          return response;
        })
        .catch((error) => {
          console.log("error getting students", error);
          this.students = [];
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    studentSelected(student) {
      this.$emit("studentSelected", student);
    },
  },
};
</script>

<style scoped>
.student-card {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.student-list {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

/* Mobile: use most of the screen */
@media (max-width: 600px) {
  .student-list {
    max-height: calc(100vh - 260px);
  }
}

/* Desktop */
@media (min-width: 601px) {
  .student-list {
    max-height: 520px;
  }
}
</style>